import * as DetailList from "~/components/DetailList";
import Issuer from "~/components/Issuer";
import SkeletonDetailList from "~/components/SkeletonDetailList";
import { getCountry, hasDivisions } from "~/data/getCountry";
import { STRUCTURE_HUMANIZE } from "~/hooks/use-vasp";
import { VASP, VASPLegalStructure } from "~/types/vaspid";

const EMPTY = "-";

export default function VASPIncorporation({ vasp }: { vasp?: VASP | null }) {
  return (
    <div>
      <DetailList.Header>
        <DetailList.Item mobile={12}>
          <DetailList.Title as="h2">Incorporation</DetailList.Title>
        </DetailList.Item>
      </DetailList.Header>

      {vasp ? (
        <DetailList.Content>
          {[
            {
              label: "Legal Name",
              value: vasp.legalName,
              issuer: vasp.issuers?.legalName?.issuerName,
              issuedDate: vasp.issuers?.legalName?.issuedDate || vasp.updatedAt,
            },
            {
              label: "Legal structure",
              value: STRUCTURE_HUMANIZE(vasp.legalStructure as VASPLegalStructure),
              issuer: vasp.issuers?.legalStructure?.issuerName,
              issuedDate: vasp.issuers?.legalStructure?.issuedDate || vasp.updatedAt,
            },
            {
              label: "Year Founded",
              value: vasp.yearFounded,
              issuer: vasp.issuers?.yearFounded?.issuerName,
              issuedDate: vasp.issuers?.yearFounded?.issuedDate || vasp.updatedAt,
            },
            {
              label: "Entity Category",
              value: vasp.entityCategory,
              issuer: vasp.issuers?.entityCategory?.issuerName,
              issuedDate: vasp.issuers?.entityCategory?.issuedDate || vasp.updatedAt,
            },
            {
              label: "Entity Status",
              value: vasp.entityStatus,
              issuer: vasp.issuers?.entityStatus?.issuerName,
              issuedDate: vasp.issuers?.entityStatus?.issuedDate || vasp.updatedAt,
            },
            {
              label: "Incorporation Country",
              value: getCountry(vasp.incorporationCountry),
              issuer: vasp.issuers?.incorporationCountry?.issuerName,
              issuedDate: vasp.issuers?.incorporationCountry?.issuedDate || vasp.updatedAt,
            },
            {
              label: "Business ID Number",
              value: vasp.businessNumber,
              issuer: vasp.issuers?.businessNumber?.issuerName,
              issuedDate: vasp.issuers?.businessNumber?.issuedDate || vasp.updatedAt,
            },
            {
              label: "Is Regulated?",
              value: vasp.isRegulated?.toLowerCase(),
              style: { textTransform: "capitalize" } as any,
              issuer: vasp.issuers?.isRegulated?.issuerName,
              issuedDate: vasp.issuers?.isRegulated?.issuedDate || vasp.updatedAt,
            },
            {
              label: "Jurisdiction Country",
              value: getCountry(vasp.jurisdictions),
              issuer: vasp.issuers?.jurisdictions?.issuerName,
              issuedDate: vasp.issuers?.jurisdictions?.issuedDate || vasp.updatedAt,
            },
            ...(hasDivisions(vasp.jurisdictions)
              ? [
                  {
                    label: "Division",
                    value: getCountry(vasp.jurisdictions, vasp.division),
                    issuer: vasp.issuers?.division?.issuerName,
                    issuedDate: vasp.issuers?.division?.issuedDate || vasp.updatedAt,
                  },
                ]
              : []),
            {
              label: "Legal Entity Identifier",
              value: vasp.leiNumber,
              issuer: vasp.issuers?.leiNumber?.issuerName,
              issuedDate: vasp.issuers?.leiNumber?.issuedDate || vasp.updatedAt,
            },
            {
              label: "Other Legal Name",
              value: vasp.otherLegalName,
              issuer: vasp.issuers?.otherLegalName?.issuerName,
              issuedDate: vasp.issuers?.otherLegalName?.issuedDate || vasp.updatedAt,
              tablet: 12,
              desktop: 12,
            },
          ].map(({ label, issuer, issuedDate, value, style, tablet, desktop }) => (
            <DetailList.Item key={label} mobile={12} tablet={tablet || 6} desktop={desktop || 3}>
              <DetailList.Term>{label}</DetailList.Term>
              <DetailList.Description style={style} className="localize-ignore">
                {value || EMPTY}
              </DetailList.Description>
              {value && (
                <Issuer
                  shortLabel={
                    issuer === "VASPnet"
                      ? "VERIFIED BY VASPNET"
                      : issuer === "GLEIF"
                      ? "VERIFIED BY GLEIF"
                      : "SELF-REPORTED"
                  }
                  longLabel={
                    issuer === "VASPnet" || issuer === "GLEIF" ? "VERIFIED INFORMATION" : "SELF-REPORTED INFORMATION"
                  }
                  name={issuer || vasp.name || ""}
                  issuedDate={issuedDate}
                  logo={`/assets/${
                    issuer === "VASPnet" ? "vasp-net-short" : issuer === "GLEIF" ? "gleif" : "self-report-logo"
                  }.svg`}
                />
              )}
            </DetailList.Item>
          ))}
        </DetailList.Content>
      ) : (
        <SkeletonDetailList count={8} />
      )}
    </div>
  );
}
